import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Crown, Star, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { motion, AnimatePresence } from "framer-motion";
import UpgradeDialog from "@/components/UpgradeDialog";
import { useAuth } from "@/context/AuthContext";
import {
  INDIVIDUAL_PLANS, BUSINESS_PLANS, INDIVIDUAL_COMPARISON, BUSINESS_COMPARISON,
} from "@/data/site";

const EASE = [0.22, 1, 0.36, 1];

export default function Pricing() {
  const [tab, setTab] = useState("individual");
  const plans = tab === "individual" ? INDIVIDUAL_PLANS : BUSINESS_PLANS;
  const comparison = tab === "individual" ? INDIVIDUAL_COMPARISON : BUSINESS_COMPARISON;

  return (
    <section id="pricing" className="relative scroll-mt-24 py-20 sm:py-28" data-testid="pricing">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="mx-auto max-w-2xl text-center">
          <p className="eyebrow">Lifetime Plans & Pricing</p>
          <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">
            Choose the plan that fits you
          </h2>
          <p className="mt-4 text-muted-foreground">
            Individual or business — all with lifetime access and a single one-time payment.
          </p>
        </Reveal>

        <Reveal delay={0.1} className="mt-8 flex justify-center">
          <div className="inline-flex rounded-full border border-border bg-card/60 p-1" data-testid="pricing-toggle">
            {[
              { k: "individual", label: "Individual Plans" },
              { k: "business", label: "Business Plans" },
            ].map((opt) => (
              <button
                key={opt.k}
                onClick={() => setTab(opt.k)}
                data-testid={`pricing-toggle-${opt.k}`}
                className={`relative rounded-full px-5 py-2 text-sm font-semibold transition-colors ${
                  tab === opt.k ? "text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {tab === opt.k && (
                  <motion.span layoutId="pricing-pill" className="absolute inset-0 -z-10 rounded-full bg-primary" transition={{ duration: 0.3, ease: EASE }} />
                )}
                {opt.label}
              </button>
            ))}
          </div>
        </Reveal>

        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3"
          >
            {plans.map((p) => (
              <PlanCard key={p.tier} plan={p} />
            ))}
          </motion.div>
        </AnimatePresence>

        <Reveal className="mt-16">
          <h3 className="font-display text-xl font-semibold">
            {tab === "individual" ? "Individual" : "Business"} Plan Comparison
          </h3>
          <div className="mt-5 overflow-x-auto rounded-2xl border border-border">
            <table className="w-full min-w-[720px] border-collapse text-left text-sm" data-testid="comparison-table">
              <thead>
                <tr className="bg-card/70">
                  {comparison.headers.map((h) => (
                    <th key={h} className="whitespace-nowrap px-5 py-4 font-display text-xs font-semibold uppercase tracking-wider text-gold">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {comparison.rows.map((row) => (
                  <tr key={row[0]} className="border-t border-border transition-colors hover:bg-accent/40">
                    {row.map((cell, ci) => (
                      <td key={`${row[0]}-${ci}`} className={`px-5 py-4 align-top ${ci === 0 ? "font-semibold text-foreground" : "text-muted-foreground"}`}>
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

const PlanCard = ({ plan }) => {
  const { user } = useAuth();
  const premium = plan.buyLink && !plan.highlight;
  const isPaid = plan.price !== "Free";
  const slug = plan.tier.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  const owned = user && plan.planKey && user.plan_key === plan.planKey;
  const cls = plan.highlight
    ? "border-gold/50 bg-card/80 shadow-[0_0_40px_-12px_hsl(var(--gold)/0.45)]"
    : premium
    ? "border-gold/30 bg-gradient-to-b from-gold/[0.07] to-transparent hover:border-gold/60 hover:shadow-[0_0_34px_-14px_hsl(var(--gold)/0.4)]"
    : "border-border bg-card/40 hover:border-sage/40";
  const labelColor = plan.highlight || premium ? "text-gold" : "text-sage";
  return (
    <div
      data-testid={`plan-${plan.tier.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className={`relative flex flex-col overflow-hidden rounded-2xl border p-6 transition-all duration-300 hover:-translate-y-1 ${cls}`}
    >
      <div className="pointer-events-none absolute right-0 top-0 h-24 w-24 overflow-hidden rounded-tr-2xl" data-testid={`plan-ribbon-${plan.tier.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
        <span className={`absolute right-[-40px] top-[18px] w-[150px] rotate-45 py-1 text-center text-[10px] font-extrabold uppercase tracking-widest text-[#0a0f0c] shadow-md ${isPaid ? "bg-gold" : "bg-sage"}`}>
          {isPaid ? "Lifetime" : "Free"}
        </span>
      </div>

      <span className={`inline-flex items-center gap-1.5 pr-14 text-xs font-semibold uppercase tracking-[0.18em] ${labelColor}`}>
        {plan.highlight ? <Star className="h-3.5 w-3.5 fill-current" /> : premium ? <Crown className="h-3.5 w-3.5" /> : null}
        {plan.badge}
      </span>
      <h3 className="mt-3 font-display text-xl font-bold">{plan.tier}</h3>
      <div className="mt-3 flex items-baseline gap-2">
        <span className="font-display text-3xl font-extrabold text-foreground">{plan.price}</span>
        <span className="text-sm text-muted-foreground">· {plan.meta}</span>
      </div>
      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{plan.blurb}</p>
      <ul className="mt-6 space-y-3">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-sm">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-sage" />
            <span className="text-foreground/90">{f}</span>
          </li>
        ))}
      </ul>
      <div className="mt-6 flex-1" />
      {plan.buyLink ? (
        <UpgradeDialog plan={plan} />
      ) : owned ? (
        <div
          data-testid={`owned-${slug}`}
          className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full border border-sage/40 bg-sage/10 px-4 py-2.5 text-sm font-semibold text-sage"
        >
          <Check className="h-4 w-4" /> Owned
        </div>
      ) : null}
      <Link
        to={`/plans/${slug}`}
        data-testid={`plan-details-${slug}`}
        className="mt-3 inline-flex items-center justify-center gap-1 text-xs font-medium text-muted-foreground transition-colors hover:text-gold"
      >
        View full details <ArrowRight className="h-3 w-3" />
      </Link>
    </div>
  );
};
